import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import TextToSpeech from '../TextToSpeech';

const AssessmentResultCard = ({ metadata, isCritical, answers }) => {
  const [expanded, setExpanded] = useState(false);
  const hasAnswers = answers.length > 0;
  const accentColor = isCritical ? 'error' : 'success';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
      <Box
        sx={{
          bgcolor: `${accentColor}.light`,
          border: 2,
          borderColor: `${accentColor}.main`,
          px: 2.5,
          py: 2,
          borderRadius: 2,
          maxWidth: '90%',
          width: hasAnswers ? '90%' : 'auto',
        }}
      >
        {/* Header: score summary */}
        <Typography variant="subtitle2" fontWeight={700} color={`${accentColor}.dark`} sx={{ mb: 0.5 }}>
          {isCritical ? 'CRITICAL ALERT — ' : ''}{metadata.testName || 'Assessment'} Result
        </Typography>
        <Typography variant="h6" fontWeight={700} color={`${accentColor}.dark`}>
          Score: {metadata.score}{metadata.maxScore ? `/${metadata.maxScore}` : ''} — {metadata.severityLevel || 'Completed'}
        </Typography>
        {metadata.interpretation && (
          <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
            {metadata.interpretation}
          </Typography>
        )}

        {/* Expandable question details */}
        {hasAnswers && (
          <Box sx={{ mt: 1.5 }}>
            <Box
              onClick={() => setExpanded(!expanded)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            >
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                View Question Details ({answers.length} items)
              </Typography>
              <IconButton size="small" sx={{ ml: 0.5 }}>
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Collapse in={expanded}>
              <TableContainer sx={{ mt: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, width: '8%' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '25%' }}>Response</TableCell>
                      <TableCell sx={{ fontWeight: 700, width: '10%', textAlign: 'center' }}>Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {answers.map((a, idx) => (
                      <TableRow key={a.questionId || idx} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{a.questionText || a.questionId || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{a.answerLabel || '—'}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 600 }}>{a.answerValue ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const TrainingMessageBubble = ({ message, onTurnClick, voiceConfig, autoplayEnabled, token, platformSettings }) => {
  const [hovered, setHovered] = useState(false);
  const { actor, content, turnIndex, metadata } = message;

  if (actor === 'system') {
    const isAssessment = metadata?.isAssessmentResult;
    const isCritical = metadata?.hasCriticalAlerts;
    const answers = metadata?.answers || [];

    if (isAssessment) {
      return <AssessmentResultCard metadata={metadata} isCritical={isCritical} answers={answers} />;
    }

    // A suggested goal. Recognised by the live event's flag OR the saved
    // functionCall, because after a reload messages come from the transcript,
    // whose strict metadata schema has already dropped isGoalProposal.
    const goalArgs = message.functionCall?.name === 'propose_goal' ? message.functionCall.args : null;
    if (metadata?.isGoalProposal || goalArgs) {
      const goalDescription = metadata?.description || goalArgs?.description || content;
      const goalType = metadata?.goalType || goalArgs?.goalType;
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }} data-testid="goal-proposal-line">
          <Box sx={{ bgcolor: 'success.light', px: 2.5, py: 1, borderRadius: 2, maxWidth: '85%' }}>
            <Typography variant="body2" color="success.dark">
              <strong>Goal suggested:</strong> {goalDescription}
              {goalType ? ` · ${goalType === 'habit' ? 'Habit' : 'One-off task'}` : ''}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
        <Box sx={{ bgcolor: 'info.light', px: 2.5, py: 1, borderRadius: 2, maxWidth: '85%' }}>
          <Typography variant="body2" color="info.dark">
            {content}
          </Typography>
        </Box>
      </Box>
    );
  }

  const isStudent = actor === 'student';
  const isPatient = actor === 'patient';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isStudent ? 'flex-end' : 'flex-start',
        mb: 1.5,
        position: 'relative',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box sx={{ maxWidth: '70%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
          {isStudent ? 'You' : 'Patient'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: isStudent ? 'row-reverse' : 'row' }}>
          <Box
            sx={{
              bgcolor: isStudent ? 'primary.main' : 'grey.100',
              color: isStudent ? 'primary.contrastText' : 'text.primary',
              borderRadius: isStudent ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              px: 2,
              py: 1.5,
              position: 'relative',
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {content}
            </Typography>
          </Box>

          {/* TTS play button for patient messages */}
          {isPatient && token && (() => {
            // Resolve voice: scenario-specific first, then platform fallback
            const scenarioVoice = voiceConfig?.patientVoiceId || null;
            const fallbackVoice = platformSettings?.voiceSettings?.defaultAITherapistVoiceId
              || platformSettings?.defaultElevenLabsVoiceId
              || null;
            const resolvedVoice = scenarioVoice || fallbackVoice;

            return (
              <Box sx={{ ml: 1 }}>
                <TextToSpeech
                  text={content}
                  size="small"
                  useBackendTTS={false}
                  token={token}
                  ttsInterface="training_chat"
                  voiceId={resolvedVoice}
                  autoplay={autoplayEnabled}
                  useStreaming={true}
                />
              </Box>
            );
          })()}
        </Box>

        {/* Turn number chip on hover */}
        {hovered && turnIndex != null && (
          <Box sx={{ display: 'flex', justifyContent: isStudent ? 'flex-end' : 'flex-start', mt: 0.5 }}>
            <Chip
              label={`Turn ${turnIndex}`}
              size="small"
              variant="outlined"
              icon={onTurnClick ? <NoteAddIcon fontSize="small" /> : undefined}
              onClick={onTurnClick ? () => onTurnClick(turnIndex) : undefined}
              sx={{ cursor: onTurnClick ? 'pointer' : 'default', fontSize: '0.7rem', height: 20 }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TrainingMessageBubble;
